import React, { useState, useEffect } from "react";
import { PublicNav, PublicFooter } from "@/components/public/PublicChrome";
import { api, formatIDR } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Check, CalendarClock, MapPin, UserCog, LogIn, LogOut, Loader2, History, XCircle } from "lucide-react";

const DAYS = ["Min", "Sen", "Sel", "Rab", "Kam", "Jum", "Sab"];
const MONTHS = ["Januari","Februari","Maret","April","Mei","Juni","Juli","Agustus","September","Oktober","November","Desember"];

export default function Booking() {
  const [month, setMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [avail, setAvail] = useState(null);
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", visit_type: "", notes: "", duration: 30 });
  const [confirmed, setConfirmed] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [branchId, setBranchId] = useState("main");
  const branches = cfg ? [{ ...cfg, id: "main" }, ...(cfg.branches || [])] : [];
  const loc = branches.find((b) => b.id === branchId) || cfg;

  useEffect(() => {
    api.get("/settings/booking").then(({ data }) => {
      setCfg(data);
      if (data?.visit_types?.length) setForm((f) => ({ ...f, visit_type: data.visit_types[0] }));
    }).catch(() => {});
  }, []);

  // Member FOMU (JWT) — pilih customer terdaftar & lihat history (token berbagi dgn Kalkulator)
  const [memberTok, setMemberTok] = useState(() => localStorage.getItem("fomu_member_token") || "");
  const [member, setMember] = useState(null);
  const [showMemberLogin, setShowMemberLogin] = useState(false);
  const [memberForm, setMemberForm] = useState({ email: "", password: "" });
  const [memberBusy, setMemberBusy] = useState(false);
  const [custList, setCustList] = useState([]);
  const [custQuery, setCustQuery] = useState("");
  const [showCustDrop, setShowCustDrop] = useState(false);
  const [pickedCust, setPickedCust] = useState(null);
  const [custHistory, setCustHistory] = useState(null);
  const [histLoading, setHistLoading] = useState(false);
  const authH = { headers: { Authorization: `Bearer ${memberTok}` } };

  useEffect(() => {
    if (!memberTok) { setMember(null); return; }
    api.get("/member/me", authH)
      .then(({ data }) => { setMember(data); api.get("/member/customers", authH).then(({ data: cs }) => setCustList(cs)).catch(() => {}); })
      .catch(() => { setMember(null); setMemberTok(""); localStorage.removeItem("fomu_member_token"); });
  }, [memberTok]);

  const memberLogin = async () => {
    setMemberBusy(true);
    try {
      const { data } = await api.post("/member/login", { email: memberForm.email, password: memberForm.password });
      localStorage.setItem("fomu_member_token", data.token);
      setMemberTok(data.token); setMember(data.member);
      setShowMemberLogin(false); setMemberForm({ email: "", password: "" });
      toast.success(`Masuk sebagai member FOMU (${data.member.role})`);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Login gagal");
    }
    setMemberBusy(false);
  };
  const memberLogout = () => { setMemberTok(""); setMember(null); localStorage.removeItem("fomu_member_token"); setCustList([]); setCustQuery(""); setPickedCust(null); setCustHistory(null); toast.success("Keluar dari mode member"); };
  const custMatches = custList.filter((c) => { const q = custQuery.toLowerCase(); return !q || [c.name, c.company, c.whatsapp, c.email].some((v) => (v || "").toLowerCase().includes(q)); }).slice(0, 6);
  const pickCustomer = async (c) => {
    setForm((f) => ({ ...f, name: c.name || "", phone: c.whatsapp || "", email: c.email || "" }));
    setCustQuery(c.name || ""); setShowCustDrop(false); setPickedCust(c); setCustHistory(null); setHistLoading(true);
    try { const { data } = await api.get(`/member/customers/${c.id}/history`, authH); setCustHistory(data); } catch { setCustHistory(null); }
    setHistLoading(false);
  };

  const y = month.getFullYear(), m = month.getMonth();
  const firstDay = new Date(y, m, 1).getDay();
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const today = new Date(); today.setHours(0, 0, 0, 0);

  const pickDate = async (day) => {
    const d = new Date(y, m, day);
    if (d < today) return;
    const ds = `${y}-${String(m + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    setSelectedDate(ds); setSlot(null); setLoadingSlots(true);
    try {
      const { data } = await api.get(`/booking/availability?date_str=${ds}&branch_id=${branchId}`);
      setAvail(data);
    } catch { toast.error("Gagal memuat slot"); }
    setLoadingSlots(false);
  };

  const submit = async () => {
    if (!form.name || !form.phone) { toast.error("Isi nama & nomor telepon"); return; }
    try {
      const { data } = await api.post("/booking", { date: selectedDate, time: slot, ...form, branch_id: branchId, duration: form.visit_type?.toLowerCase().startsWith("pengambilan") ? 30 : form.duration });
      setConfirmed(data);
      toast.success("Booking dikonfirmasi!");
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal booking"); }
  };

  if (confirmed) {
    return (
      <div>
        <PublicNav />
        <div className="max-w-md mx-auto px-5 py-20 text-center anim-up">
          <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto"><Check className="w-8 h-8" /></div>
          <h1 className="font-head text-2xl font-bold mt-5">Booking Dikonfirmasi</h1>
          <p className="text-slate-600 mt-2">Terima kasih, {confirmed.name}! Kunjungan Anda terjadwal.</p>
          <div className="bg-white border border-slate-200 rounded-2xl p-5 mt-6 text-left font-mono text-sm space-y-2">
            <div className="flex justify-between"><span className="text-slate-500">Kode</span><span>{confirmed.code}</span></div>
            {confirmed.branch_name && <div className="flex justify-between gap-3"><span className="text-slate-500">Lokasi</span><span className="text-right" data-testid="confirmed-branch">{confirmed.branch_name}{loc?.address ? <span className="block text-xs text-slate-400">{loc.address}</span> : null}</span></div>}
            <div className="flex justify-between"><span className="text-slate-500">Tanggal</span><span>{confirmed.date}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Jam</span><span>{confirmed.time}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Status</span><span className="text-emerald-600">{confirmed.status}</span></div>
          </div>
          <a href="/" className="inline-block mt-6 text-sm text-slate-500 hover:text-slate-900">← Kembali ke beranda</a>
        </div>
        <PublicFooter />
      </div>
    );
  }

  return (
    <div>
      <PublicNav />
      <div className="max-w-5xl mx-auto px-5 py-10">
        <div className="flex items-center gap-2 text-sky-600 text-sm font-mono"><CalendarClock className="w-4 h-4" /> BOOKING KONSULTASI</div>
        <h1 className="font-head text-3xl font-bold tracking-tight mt-2">Jadwalkan Kunjungan</h1>
        <p className="text-slate-600 mt-1">Pilih tanggal & jam. Slot tersedia setiap 30 menit. (Senin–Jumat 10:00–17:00, Sabtu 10:00–14:00)</p>

        {branches.length > 1 && (
          <div className="mt-6 flex flex-wrap gap-2" data-testid="branch-picker">
            <span className="text-sm text-slate-600 self-center mr-1">Pilih cabang:</span>
            {branches.map((b) => (
              <button key={b.id} data-testid={`branch-opt-${b.id}`} onClick={() => { setBranchId(b.id); setSelectedDate(null); setSlot(null); }} className={`px-4 py-2 rounded-full text-sm border transition-colors ${branchId === b.id ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 hover:border-slate-500"}`}>{b.office_name || "Cabang"}</button>
            ))}
          </div>
        )}
        <div className="mt-6 grid md:grid-cols-[1fr_1.4fr] gap-4 bg-white border border-slate-200 rounded-2xl p-4" data-testid="fomu-location">
          <div>
            <div className="flex items-center gap-2 text-slate-900 font-medium"><MapPin className="w-4 h-4 text-sky-500" /> {loc?.office_name || "FOMU 3D PRINT"}</div>
            <p className="text-sm text-slate-500 mt-2">{loc?.address || "Jakarta, Indonesia"}</p>
            <p className="text-xs text-slate-400 mt-2">{loc?.hours || "Senin–Jumat 10:00–17:00 · Sabtu 10:00–14:00 · Minggu tutup"}</p>
            {(loc?.phone || loc?.email) && (
              <p className="text-xs text-slate-500 mt-2">{loc?.phone && <>WA {loc.phone}</>}{loc?.phone && loc?.email && " · "}{loc?.email}</p>
            )}
            <a href={`https://www.google.com/maps/search/?api=1&query=${loc?.lat ?? -6.165075},${loc?.lng ?? 106.767943}`} target="_blank" rel="noreferrer" className="inline-block mt-3 text-sm text-sky-600 hover:underline">Buka di Google Maps →</a>
          </div>
          <iframe title="FOMU Location" className="w-full h-52 rounded-xl border border-slate-200" src={`https://www.openstreetmap.org/export/embed.html?bbox=${(loc?.lng ?? 106.767943) - 0.01}%2C${(loc?.lat ?? -6.165075) - 0.008}%2C${(loc?.lng ?? 106.767943) + 0.01}%2C${(loc?.lat ?? -6.165075) + 0.008}&layer=mapnik&marker=${loc?.lat ?? -6.165075}%2C${loc?.lng ?? 106.767943}`} />
        </div>

        <div className="grid md:grid-cols-2 gap-6 mt-8">
          {/* Calendar */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <button data-testid="prev-month" onClick={() => setMonth(new Date(y, m - 1, 1))} className="w-9 h-9 rounded-lg border border-slate-200 flex items-center justify-center"><ChevronLeft className="w-4 h-4" /></button>
              <span className="font-head font-semibold">{MONTHS[m]} {y}</span>
              <button data-testid="next-month" onClick={() => setMonth(new Date(y, m + 1, 1))} className="w-9 h-9 rounded-lg border border-slate-200 flex items-center justify-center"><ChevronRight className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-7 gap-1 text-center text-xs text-slate-400 mb-2">{DAYS.map((d) => <div key={d}>{d}</div>)}</div>
            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: firstDay }).map((_, i) => <div key={"e" + i} />)}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1;
                const d = new Date(y, m, day);
                const ds = `${y}-${String(m + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                const isSunday = d.getDay() === 0;
                const isPast = d < today;
                const disabled = isPast || isSunday;
                const active = selectedDate === ds;
                return (
                  <button key={day} data-testid={`day-${day}`} disabled={disabled} onClick={() => pickDate(day)}
                    className={`aspect-square rounded-lg text-sm transition-colors ${active ? "bg-slate-900 text-white" : disabled ? "text-slate-300 cursor-not-allowed" : "hover:bg-sky-50 text-slate-700"}`}>
                    {day}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Slots + form */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            {!selectedDate ? (
              <div className="h-full flex items-center justify-center text-slate-400 text-sm py-16">Pilih tanggal terlebih dahulu</div>
            ) : loadingSlots ? (
              <div className="py-16 text-center text-slate-400 text-sm">Memuat slot…</div>
            ) : avail?.status !== "OPEN" ? (
              <div className="py-16 text-center text-slate-500">{avail?.reason || "Tidak tersedia"}</div>
            ) : (
              <>
                <div className="text-sm font-medium mb-3">Slot tersedia · {selectedDate}</div>
                <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                  {avail.slots.map((s) => (
                    <button key={s.time} data-testid={`slot-${s.time}`} disabled={!s.available} onClick={() => setSlot(s.time)}
                      title={s.full ? `Penuh (${s.count}/${s.capacity}) — pilih jam lain` : s.busy ? `Hampir penuh (${s.count}/${s.capacity})` : `${s.count}/${s.capacity} orang`}
                      className={`relative py-2 rounded-lg text-xs border transition-colors flex flex-col items-center ${slot === s.time ? "bg-slate-900 text-white border-slate-900" : s.full || !s.available ? "border-slate-100 text-slate-300 line-through cursor-not-allowed bg-slate-50" : s.busy ? "border-amber-300 bg-amber-50 text-amber-700" : "border-slate-200 hover:border-sky-400"}`}>
                      <span className="text-sm">{s.time}</span>
                      <span className={`text-[9px] font-mono ${s.full ? "text-rose-400" : s.busy ? "text-amber-600" : "text-slate-400"}`}>{s.full ? "PENUH" : `${s.count}/${s.capacity}`}</span>
                      {s.busy && !s.full && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-amber-400" />}
                    </button>
                  ))}
                </div>
                {slot && (
                  <div className="mt-5 space-y-3 border-t border-slate-100 pt-4 anim-up">
                    {member && (
                      <div className="relative" data-testid="bk-customer-lookup">
                        <input data-testid="bk-customer-search" placeholder="Cari customer terdaftar (nama / WA / email)…" value={custQuery}
                          onChange={(e) => { setCustQuery(e.target.value); setShowCustDrop(true); setPickedCust(null); setCustHistory(null); }}
                          onFocus={() => setShowCustDrop(true)}
                          className="w-full border border-sky-300 bg-sky-50 rounded-lg px-3 py-2 text-sm" />
                        {showCustDrop && custMatches.length > 0 && (
                          <div className="absolute left-0 right-0 top-full mt-1 z-20 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-auto">
                            {custMatches.map((c) => (
                              <button key={c.id} data-testid={`bk-customer-opt-${c.id}`} onClick={() => pickCustomer(c)} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b border-slate-50 last:border-0">
                                <div className="font-medium">{c.name}{c.company ? ` · ${c.company}` : ""}</div>
                                <div className="text-[11px] text-slate-500">{c.whatsapp || "-"} · {c.email || "-"}</div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    {member && pickedCust && (
                      <div className="border border-sky-200 bg-sky-50 rounded-lg p-3 text-xs" data-testid="bk-customer-history">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-sky-700 inline-flex items-center gap-1.5"><History className="w-3.5 h-3.5" /> History: {pickedCust.name}</span>
                          <button data-testid="bk-history-close" onClick={() => { setPickedCust(null); setCustHistory(null); setCustQuery(""); }} className="text-slate-400 hover:text-rose-500"><XCircle className="w-4 h-4" /></button>
                        </div>
                        {histLoading ? <div className="text-slate-400 mt-2">Memuat history…</div> : custHistory && (
                          <div className="mt-2 space-y-2">
                            <div>
                              <div className="font-mono text-[10px] text-slate-400 mb-1">QUOTATION ({custHistory.quotations.length})</div>
                              {custHistory.quotations.length === 0 && <div className="text-slate-400 italic">Belum ada quotation.</div>}
                              {custHistory.quotations.slice(0, 5).map((q) => (
                                <div key={q.id} data-testid={`bk-hist-quote-${q.code}`} className="flex justify-between gap-2 py-0.5">
                                  <span className="truncate">{q.code} · {q.status}{q.project ? ` · ${q.project}` : ""}</span>
                                  <span className="shrink-0 font-mono">{formatIDR(q.total)}</span>
                                </div>
                              ))}
                            </div>
                            <div>
                              <div className="font-mono text-[10px] text-slate-400 mb-1">KUNJUNGAN ({custHistory.bookings.length})</div>
                              {custHistory.bookings.length === 0 && <div className="text-slate-400 italic">Belum ada kunjungan.</div>}
                              {custHistory.bookings.slice(0, 5).map((b) => (
                                <div key={b.id} data-testid={`bk-hist-booking-${b.code}`} className="flex justify-between gap-2 py-0.5">
                                  <span className="truncate">{b.code} · {b.visit_type || "Kunjungan"}</span>
                                  <span className="shrink-0 font-mono">{b.date} {b.time}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    <input data-testid="bk-name" placeholder="Nama *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                    <input data-testid="bk-phone" placeholder="No. Telepon *" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                    <input data-testid="bk-email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                    <div>
                      <label className="text-xs text-slate-500 font-mono">KEBUTUHAN KEDATANGAN</label>
                      <select data-testid="bk-visit-type" value={form.visit_type} onChange={(e) => setForm({ ...form, visit_type: e.target.value })} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                        {(cfg?.visit_types || ["Konsultasi 3D printing", "Demo mesin/produk", "Pengambilan barang", "Meeting dengan sales", "Konsultasi project", "Service/maintenance", "Lain-lain"]).map((v) => <option key={v} value={v}>{v}</option>)}
                      </select>
                    </div>
                    {avail.slots.find((s) => s.time === slot)?.busy && (
                      <div data-testid="busy-note" className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">Jam ini ramai — ada antrian tamu. Anda tetap bisa booking & menunggu sebentar saat tiba.</div>
                    )}
                    <div>
                      <label className="text-xs text-slate-500 font-mono">DURASI KUNJUNGAN</label>
                      {form.visit_type?.toLowerCase().startsWith("pengambilan") ? (
                        <div className="mt-1 text-sm text-slate-600">Pengambilan barang — 30 menit</div>
                      ) : (
                        <select data-testid="bk-duration" value={form.duration} onChange={(e) => setForm({ ...form, duration: +e.target.value })} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                          <option value={30}>30 menit</option>
                          <option value={60}>1 jam</option>
                        </select>
                      )}
                    </div>
                    <textarea data-testid="bk-notes" placeholder="Catatan (opsional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                    <button data-testid="confirm-booking-btn" onClick={submit} className="w-full bg-slate-900 text-white rounded-xl py-3 font-medium hover:bg-slate-800 transition-colors">Konfirmasi Booking · {slot}</button>
                  </div>
                )}
              </>
            )}
            <div className="mt-5 border-t border-slate-100 pt-3 text-center text-[11px] text-slate-400" data-testid="bk-member-footer">
              {member ? (
                <span className="inline-flex items-center justify-center gap-1.5 flex-wrap">
                  <UserCog className="w-3.5 h-3.5 text-sky-600" />
                  <span className="text-sky-600 font-medium">MEMBER FOMU · {member.name || member.email} ({member.role})</span>
                  <span className="text-slate-300">·</span>
                  <button data-testid="bk-member-logout" onClick={memberLogout} className="text-rose-500 underline underline-offset-2 hover:text-rose-600 inline-flex items-center gap-1"><LogOut className="w-3 h-3" /> Keluar</button>
                </span>
              ) : (
                <button data-testid="bk-member-login-link" onClick={() => setShowMemberLogin(true)} className="text-sky-600 font-medium underline underline-offset-2 hover:text-sky-700 inline-flex items-center gap-1.5">
                  <UserCog className="w-3.5 h-3.5" /> Login Member FOMU
                </button>
              )}
              <div className="mt-1 text-slate-400">Member dapat memilih customer terdaftar &amp; melihat history-nya.</div>
            </div>
          </div>
        </div>
      </div>
      {showMemberLogin && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={() => setShowMemberLogin(false)} data-testid="bk-member-login-modal">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-head text-lg font-semibold inline-flex items-center gap-2"><UserCog className="w-5 h-5 text-sky-600" /> Login Member FOMU</h3>
            <p className="text-sm text-slate-500 mb-4 mt-1">Member tim dapat memilih customer yang sudah terdaftar dan melihat history-nya.</p>
            <div className="space-y-3">
              <input data-testid="bk-member-email" type="email" placeholder="Email" value={memberForm.email} onChange={(e) => setMemberForm({ ...memberForm, email: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
              <input data-testid="bk-member-password" type="password" placeholder="Password" value={memberForm.password} onChange={(e) => setMemberForm({ ...memberForm, password: e.target.value })} onKeyDown={(e) => e.key === "Enter" && memberLogin()} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <button data-testid="bk-member-login-btn" disabled={memberBusy || !memberForm.email || !memberForm.password} onClick={memberLogin} className="w-full mt-4 bg-sky-600 text-white rounded-xl py-2.5 font-medium inline-flex items-center justify-center gap-2 hover:bg-sky-500 disabled:opacity-40">
              {memberBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />} Masuk
            </button>
          </div>
        </div>
      )}
      <PublicFooter />
    </div>
  );
}
